import {
  ArcCurve,
  BufferGeometry,
  EllipseCurve,
  Float32BufferAttribute,
  Line,
  LineBasicMaterial,
  LineDashedMaterial,
  LineSegments,
  type Object3D,
  Shape,
  ShapeGeometry,
  Vector3,
} from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import { LineMaterial, type LineMaterialParameters } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { Text } from 'troika-three-text'
import {
  applyCadLineViewportResolution,
  createCadLineMaterialSync,
  createCadLineObjectSync,
  getCadLineBackend,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'

/** CAD 平面坐标点，z 省略时按 0 处理 */
export interface IPoint {
  /** X 坐标 */
  x: number
  /** Y 坐标 */
  y: number
  /** Z 坐标，省略时为 0 */
  z?: number
}

/** 线段两端点描述 */
export interface ILine {
  /** 线段起点 */
  startPoint: IPoint
  /** 线段终点 */
  endPoint: IPoint
}

/** 线型材质通用选项，透传给 three.js 材质构造器 */
export interface MaterialOption {
  /** 线条颜色，十六进制整型 */
  color?: number
  /** 线宽，单位像素（部分材质为世界单位） */
  linewidth?: number
  /** 不透明度，0~1 */
  opacity?: number
  /** 是否启用透明度混合 */
  transparent?: boolean
  /** 是否开启深度测试 */
  depthTest?: boolean
  /** 其他透传字段 */
  [key: string]: any
}

/** 椭圆采样选项 */
export interface EllipseOptions {
  /** 椭圆圆心 */
  center: IPoint
  /** X 轴半径 */
  radiusX: number
  /** Y 轴半径 */
  radiusY: number
  /** 起始角，单位弧度，默认 0 */
  startAngle?: number
  /** 终止角，单位弧度，默认 2π */
  endAngle?: number
  /** 是否顺时针，默认 false */
  clockwise?: boolean
  /** 整体旋转角，单位弧度，默认 0 */
  rotation?: number
  /** 采样分段数，默认 32 */
  segmentsNum?: number
}

/** CAD 绘制工具集，封装几何体构造、绘制与回收等静态方法 */
export class DrawUtils {
  /** 按当前 backend 创建工具线材质。 */
  private static createLineMaterial(materialOption: MaterialOption): LineMaterial {
    return createCadLineMaterialSync({
      backend: getCadLineBackend(),
      options: materialOption as LineMaterialParameters,
      createWebGlMaterial: (options) => new LineMaterial(options),
    })
  }

  /** 按当前 backend 创建宽线对象。 */
  private static createLineObject(geometry: LineGeometry, material: LineMaterial): Line2 {
    return createCadLineObjectSync({
      backend: getCadLineBackend(),
      geometry,
      material,
      createWebGlObject: () => new Line2(geometry, material),
    })
  }

  /**
   * 根据点列构造 BufferGeometry。
   *
   * @param points - 顶点列表，z 省略按 0
   * @returns 已设置 position 属性的几何体
   */
  static getBufferGeometryByPoints(points: IPoint[]): BufferGeometry {
    const vertices: number[] = []
    points.forEach((point) => {
      vertices.push(point.x, point.y, point.z ?? 0)
    })

    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3))

    return geometry
  }

  /**
   * 根据线段列表构造 BufferGeometry。
   *
   * @param lines - 线段列表
   * @returns 以线段端点展开的几何体
   */
  static getBufferGeometryByLines(lines: ILine[]): BufferGeometry {
    const points: IPoint[] = DrawUtils.getPointsByLines(lines)
    return DrawUtils.getBufferGeometryByPoints(points)
  }

  /**
   * 将点列展开为 Three.js 所需的扁平顶点数组。
   *
   * @param points - 顶点列表
   * @returns 长度为 points.length * 3 的数字数组
   */
  static getVerticesByPoints(points: IPoint[]): number[] {
    const vertices: number[] = []
    points.forEach((point) => {
      vertices.push(point.x, point.y, point.z || 0)
    })
    return vertices
  }

  /**
   * 将线段列表展平为端点序列，顺序 start-end 交替。
   *
   * @param lines - 线段列表
   * @returns 端点数组
   */
  static getPointsByLines(lines: ILine[]): IPoint[] {
    const points: IPoint[] = []
    lines.forEach((line) => {
      const { startPoint, endPoint } = line
      points.push(startPoint, endPoint)
    })
    return points
  }

  /**
   * 按点序列构造 Line2 所用 LineGeometry。
   *
   * @param points - 顶点列表，按绘制顺序
   * @returns 已设置 position 的 LineGeometry
   */
  static getLineGeometryByPoints(points: IPoint[]): LineGeometry {
    const vertices: number[] = DrawUtils.getVerticesByPoints(points)
    const geometry = new LineGeometry()
    geometry.setPositions(new Float32Array(vertices))

    return geometry
  }

  /**
   * 按线段列表构造 Line2 所用 LineGeometry。
   *
   * @param lines - 线段列表
   * @returns 已设置 position 的 LineGeometry
   */
  static getLineGeometryByLines(lines: ILine[]): LineGeometry {
    const points: IPoint[] = DrawUtils.getPointsByLines(lines)
    return DrawUtils.getLineGeometryByPoints(points)
  }

  /**
   * 按线段列表构造 LineSegments2 所用 LineSegmentsGeometry。
   *
   * @param lines - 线段列表
   * @returns 已设置 position 的 LineSegmentsGeometry
   */
  static getLineSegmentsGeometryByLines(lines: ILine[]): LineSegmentsGeometry {
    const points: IPoint[] = DrawUtils.getPointsByLines(lines)
    const vertices: number[] = DrawUtils.getVerticesByPoints(points)
    const geometry = new LineSegmentsGeometry()
    geometry.setPositions(new Float32Array(vertices))
    return geometry
  }

  /**
   * 绘制一条线段。
   *
   * @param point1 - 线段起点
   * @param point2 - 线段终点
   * @param materialOption - LineBasicMaterial 构造选项
   * @param parent - 可选父对象，若提供则自动挂载
   * @returns 创建的 Line 对象
   */
  static drawLine(point1: IPoint, point2: IPoint, materialOption: any, parent?: Object3D) {
    const geometry = DrawUtils.getBufferGeometryByPoints([point1, point2])
    const material = new LineBasicMaterial(materialOption)
    const object = new Line(geometry, material)

    parent?.add(object)

    return object
  }

  /**
   * 绘制多段线（连续折线）
   * @param points 多段线的顶点数组
   * @param materialOption 材质选项
   * @param closed 是否闭合（连接首尾点）
   * @param parent 父对象，如果提供则自动添加到父对象中
   * @returns 返回创建的Line对象
   */
  static drawPolyline(
    points: IPoint[],
    materialOption: MaterialOption,
    parent?: Object3D,
    closed: boolean = false
  ) {
    if (points.length < 2) {
      throw new Error('至少需要2个点来绘制多段线')
    }

    // 如果需要闭合，添加起点到末尾
    const polylinePoints = closed ? [...points, points[0]] : points
    const geometry = DrawUtils.getBufferGeometryByPoints(polylinePoints)
    const material = new LineBasicMaterial(materialOption)
    const object = new Line(geometry, material)

    parent?.add(object)

    return object
  }

  /**
   * 使用 Line2 绘制多段线，支持精确线宽。
   *
   * @param points - 多段线顶点列表，至少 2 个
   * @param materialOption - LineMaterial 构造选项
   * @param parent - 可选父对象，若提供则自动挂载
   * @param closed - 是否闭合，会把起点追加到末尾，默认 false
   * @returns 创建的 Line2 对象
   */
  static drawPolyline2(
    points: IPoint[],
    materialOption: MaterialOption,
    parent?: Object3D,
    closed: boolean = false
  ) {
    if (points.length < 2) {
      throw new Error('至少需要2个点来绘制多段线')
    }

    // 如果需要闭合，添加起点到末尾
    const polylinePoints = closed ? [...points, points[0]] : points
    const geometry = DrawUtils.getLineGeometryByPoints(polylinePoints)
    const material = DrawUtils.createLineMaterial(materialOption)
    applyCadLineViewportResolution(material, window.innerWidth, window.innerHeight)
    const object = DrawUtils.createLineObject(geometry, material)

    parent?.add(object)

    return object
  }

  /**
   * 绘制多条独立线段。
   *
   * @param lines - 线段列表
   * @param materialOption - LineBasicMaterial 构造选项
   * @param parent - 可选父对象，若提供则自动挂载
   * @returns 创建的 LineSegments 对象
   */
  static drawLineSegments(lines: ILine[], materialOption: MaterialOption, parent?: Object3D) {
    const geometry = DrawUtils.getBufferGeometryByLines(lines)
    const material = new LineBasicMaterial(materialOption)
    const object = new LineSegments(geometry, material)

    parent?.add(object)

    return object
  }

  /**
   * 使用 LineDashedMaterial 绘制虚线段。
   *
   * @param point1 - 线段起点
   * @param point2 - 线段终点
   * @param materialOption - LineDashedMaterial 构造选项
   * @param parent - 可选父对象，若提供则自动挂载
   * @returns 创建的 Line 对象
   */
  static drawDashedLine(
    point1: IPoint,
    point2: IPoint,
    materialOption: MaterialOption,
    parent?: Object3D
  ) {
    const geometry = DrawUtils.getBufferGeometryByPoints([point1, point2])
    const material = new LineDashedMaterial(materialOption)
    const object = new Line(geometry, material)
    object.computeLineDistances()

    parent?.add(object)

    return object
  }

  /**
   * 使用 Line2 绘制虚线段，支持像素线宽与 dash 参数。
   *
   * @param point1 - 线段起点
   * @param point2 - 线段终点
   * @param materialOption - LineMaterial 构造选项，含 dash 相关字段
   * @param parent - 可选父对象，若提供则自动挂载
   * @returns 创建的 Line2 对象
   */
  static drawDashedLine2(
    point1: IPoint,
    point2: IPoint,
    materialOption: MaterialOption,
    parent?: Object3D
  ) {
    const geometry = DrawUtils.getLineGeometryByPoints([point1, point2])
    const material = DrawUtils.createLineMaterial(materialOption)
    const object = DrawUtils.createLineObject(geometry, material)
    object.computeLineDistances()

    parent?.add(object)

    return object
  }

  /**
   * 使用 LineDashedMaterial 绘制虚线多段线。
   *
   * @param points - 多段线顶点列表，至少 2 个
   * @param materialOption - LineDashedMaterial 构造选项
   * @param parent - 可选父对象，若提供则自动挂载
   * @param closed - 是否闭合，会把起点追加到末尾，默认 false
   * @returns 创建的 Line 对象
   */
  static drawDashedPolyline(
    points: IPoint[],
    materialOption: MaterialOption,
    parent?: Object3D,
    closed: boolean = false
  ) {
    if (points.length < 2) {
      throw new Error('至少需要2个点来绘制多段线')
    }

    // 如果需要闭合，添加起点到末尾
    const polylinePoints = closed ? [...points, points[0]] : points
    const geometry = DrawUtils.getBufferGeometryByPoints(polylinePoints)
    const material = new LineDashedMaterial(materialOption)
    const object = new Line(geometry, material)
    object.computeLineDistances()

    parent?.add(object)

    return object
  }

  /**
   * 使用 LineDashedMaterial 绘制多条虚线段。
   *
   * @param lines - 线段列表
   * @param materialOption - LineDashedMaterial 构造选项
   * @param parent - 可选父对象，若提供则自动挂载
   * @returns 创建的 LineSegments 对象
   */
  static drawDashedLineSegments(
    lines: ILine[],
    materialOption: MaterialOption,
    parent?: Object3D
  ): LineSegments {
    const geometry = DrawUtils.getBufferGeometryByLines(lines)
    const material = new LineDashedMaterial(materialOption)
    const object = new LineSegments(geometry, material)
    object.computeLineDistances()

    parent?.add(object)

    return object
  }

  /**
   * 按椭圆参数采样得到点列。
   *
   * @param options - 椭圆采样配置，见 EllipseOptions
   * @returns 采样点列表
   */
  static getEllipsePoints(options: EllipseOptions): IPoint[] {
    const {
      center,
      radiusX,
      radiusY,
      startAngle = 0,
      endAngle = Math.PI * 2,
      clockwise = false,
      rotation = 0,
      segmentsNum = 32,
    } = options
    const ellipseCurve = new EllipseCurve(
      center.x,
      center.y,
      radiusX,
      radiusY,
      startAngle,
      endAngle,
      clockwise,
      rotation
    )
    const points2D = ellipseCurve.getPoints(segmentsNum)
    const points: IPoint[] = points2D.map((p) => ({ x: p.x, y: p.y }))
    return points
  }

  /**
   * 按圆弧参数采样得到点列。
   *
   * @param options - 圆弧采样配置：圆心、半径、起止角、方向、分段数
   * @returns 采样点列表
   */
  static getArcPoints(options: {
    center: IPoint
    radius: number
    startAngle?: number
    endAngle?: number
    clockwise?: boolean
    segmentsNum?: number
  }): IPoint[] {
    const {
      center,
      radius,
      startAngle = 0,
      endAngle = Math.PI * 2,
      clockwise = false,
      segmentsNum = 20,
    } = options
    const arcCurve = new ArcCurve(center.x, center.y, radius, startAngle, endAngle, clockwise)
    const points2D = arcCurve.getPoints(segmentsNum)
    const points: IPoint[] = points2D.map((p) => ({ x: p.x, y: p.y }))
    return points
  }

  /**
   * 按圆弧参数构造 BufferGeometry。
   *
   * @param options - 圆弧采样配置
   * @returns 已设置 position 的 BufferGeometry
   */
  static getArcGeometry(options: {
    center: IPoint
    radius: number
    startAngle?: number
    endAngle?: number
    clockwise?: boolean
    segmentsNum?: number
  }) {
    const points = DrawUtils.getArcPoints(options)
    const geometry = DrawUtils.getBufferGeometryByPoints(points)
    return geometry
  }

  /**
   * 使用 LineBasicMaterial 绘制实线圆弧。
   *
   * @param options - 圆弧参数及材质选项、可选父对象
   * @returns 创建的 Line 对象
   */
  static drawArc(options: {
    center: IPoint
    radius: number
    startAngle?: number
    endAngle?: number
    clockwise?: boolean
    segmentsNum?: number
    materialOption: MaterialOption
    parent?: Object3D
  }) {
    const { materialOption, parent } = options
    const geometry = DrawUtils.getArcGeometry(options)
    const material = new LineBasicMaterial(materialOption)
    const object = new Line(geometry, material)

    parent?.add(object)

    return object
  }

  /**
   * 使用 LineDashedMaterial 绘制虚线圆弧。
   *
   * @param options - 圆弧参数及材质选项、可选父对象
   * @returns 创建的 Line 对象
   */
  static drawDashedArc(options: {
    center: IPoint
    radius: number
    startAngle?: number
    endAngle?: number
    clockwise?: boolean
    segmentsNum?: number
    materialOption: MaterialOption
    parent?: Object3D
  }) {
    const { materialOption, parent } = options
    const geometry = DrawUtils.getArcGeometry(options)
    const material = new LineDashedMaterial(materialOption)
    const object = new Line(geometry, material)
    object.computeLineDistances()

    parent?.add(object)

    return object
  }

  /**
   * 使用 Line2 绘制虚线圆弧，支持像素线宽与 dash 参数。
   *
   * @param options - 圆弧参数及 LineMaterial 选项、可选父对象
   * @returns 创建的 Line2 对象
   */
  static drawDashedArc2(options: {
    center: IPoint
    radius: number
    startAngle?: number
    endAngle?: number
    clockwise?: boolean
    segmentsNum?: number
    materialOption: MaterialOption
    parent?: Object3D
  }) {
    const { materialOption, parent } = options
    const points = DrawUtils.getArcPoints(options)
    const geometry = DrawUtils.getLineGeometryByPoints(points)
    const material = DrawUtils.createLineMaterial(materialOption)
    const object = DrawUtils.createLineObject(geometry, material)
    object.computeLineDistances()

    parent?.add(object)

    return object
  }

  /**
   * 构造朝向 -X 的等腰三角形箭头几何体，尖端在原点。
   *
   * @param length - 箭头长度，单位世界坐标
   * @param width - 箭头底宽，单位世界坐标
   * @returns 箭头的 ShapeGeometry
   */
  static createArrowShape(length: number, width: number) {
    const shape = new Shape()
    shape.moveTo(0, 0) // 尖端
    shape.lineTo(-length, width / 2)
    shape.lineTo(-length, -width / 2)
    shape.lineTo(0, 0)
    return new ShapeGeometry(shape)
  }

  /**
   * 递归释放 Object3D 及其子节点的几何体、材质、troika Text，并从父级移除。
   *
   * @param object - 目标对象，空值时直接返回
   */
  static disposeObject(object?: Object3D) {
    if (!object) {
      return
    }

    object.traverse((child) => {
      // 销毁 geometry
      // @ts-expect-error
      if (child.geometry && typeof child.geometry.dispose === 'function') {
        // @ts-expect-error
        child.geometry.dispose()
      }

      // 销毁 material
      // @ts-expect-error
      if (child.material) {
        // @ts-expect-error
        if (Array.isArray(child.material)) {
          // @ts-expect-error
          child.material.forEach((m: any) => m && typeof m.dispose === 'function' && m.dispose())
          // @ts-expect-error
        } else if (typeof child.material.dispose === 'function') {
          // @ts-expect-error
          child.material.dispose()
        }
      }

      // 销毁 texture
      if (child instanceof Text) {
        ;(child as Text).dispose()
      }
    })

    // 从父对象中移除
    if (object.parent) {
      object.parent.remove(object)
    }
  }

  /**
   * 以新点列更新既有 Line 对象；数量不一致时重建几何体。
   *
   * @param line - 待更新的 Line 对象
   * @param points - 新的顶点列表
   */
  static updateLineByPoints(line: Line, points: IPoint[]) {
    // 点的数量不一致，重新创建几何体
    if (line.geometry.attributes.position.count !== points.length) {
      const geometry = DrawUtils.getBufferGeometryByPoints(points)
      line.geometry.dispose()
      line.geometry = geometry
      return
    }
    const pointVectors = points.map((p) => new Vector3(p.x, p.y, p.z || 0))
    line.geometry.setFromPoints(pointVectors)
    line.geometry.attributes.position.needsUpdate = true
  }

  /**
   * 以新点列更新既有 Line2 对象的位置属性。
   *
   * @param line - 待更新的 Line2 对象
   * @param points - 新的顶点列表
   */
  static updateLine2ByPoints(line: Line2, points: IPoint[]) {
    const vertices = points.flatMap((p) => [p.x, p.y, p.z || 0])
    line.geometry.setPositions(vertices)
    line.geometry.attributes.position.needsUpdate = true
  }
}
